<template>
  <div class="mx-5">
    <div class="mt-5 flex justify-start space-x-2">
      <Headline size="base" type="loud">{{ $t('wishlist.heading') }}</Headline>
      <p data-test-id="wishlist-count" class="text-gray-800 text-sm">
        {{ count }} {{ $t('wishlist.products_count', count) }}
      </p>
    </div>
    <template v-if="fetching">
      <div class="mt-8 flex flex-col gap-5 md:flex-row">
        <Skeleton
          v-for="index in count"
          :key="`product-loading-${index}`"
          type="custom"
          class="h-96 w-full md:w-80"
        />
      </div>
    </template>
    <template v-else>
      <div v-if="count > 0" class="mt-8 grid w-auto grid-cols-12 gap-5">
        <template v-for="(item, idx) in orderedItems">
          <WishlistCard
            v-if="item.product"
            :key="`product-${item.product.id}-${item.variantId || ''}-${idx}`"
            data-test-id="wishlist-card"
            :item="item"
            :show-price-reduction="isActiveSalePeriod"
            :is-in-basket="
              isInBasket({ productId: item.product.id }, basket.items.value)
            "
            :image="getFrontImage(item.product.images)"
            @click:change-size="changeSize(item, $event)"
            @click:add-to-cart="addItemToCart(item)"
          />
        </template>
      </div>
      <div v-if="count === 0" class="mt-8 space-y-8">
        <div>
          <p>
            {{ $t('wishlist.no_items_info') }}
          </p>
          <p>
            {{ $t('wishlist.continue_shopping_info') }}
          </p>
        </div>

        <div class="mt-10 flex space-x-4">
          <LinkAsButton :to="toLocalePath('/')" type="tertiary">
            {{ $t('wishlist.continue_shopping_label') }}
          </LinkAsButton>
          <LinkAsButton type="primary" :to="toLocalePath({ name: 'signin' })">
            {{ $t('wishlist.sign_in_label') }}
          </LinkAsButton>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { Variant, WishlistItem } from '@scayle/storefront-nuxt'
import { getFirstAttributeValue, getVariant } from '@scayle/storefront-nuxt'
import { orderBy } from 'es-toolkit'
import { isEmpty } from 'es-toolkit/compat'
import { Action } from '#layers/base/plugins/06.toast'

const { $alert } = useNuxtApp()
const config = useRuntimeConfig()
const i18n = useI18n()
const route = useRoute()
const { trackWishlist, trackAddToBasket } = useTrackingEvents()
const { openAddToBasketFlyout } = useUiState()
const { toLocalePath } = useRouteHelpers()

const {
  data: wishlistData,
  fetching,
  count,
  replaceItem,
  fetch: fetchWishlist,
} = useAgsWishlist()
const basket = useAgsBasket()
const {
  data: shopConfiguration,
  isActiveSalePeriod,
  fetch: fetchShopConfiguration,
} = useSmartShopConfiguration()

// Fetch data on navigation
await fetchOnNavigation(async () => {
  await Promise.all([
    () => fetchWishlist(),
    () => basket.fetch(),
    () => fetchShopConfiguration(),
  ])
})

const orderedItems = computed(() =>
  orderBy(
    wishlistData.value?.items || [],
    [(item) => getFirstAttributeValue(item.product?.attributes, 'name')?.label],
    ['asc'],
  ),
)

const isInBasket = basket.contains

onMounted(() => trackWishlistItems())

const trackWishlistItems = () => {
  trackWishlist({
    wishlistItems: wishlistData.value?.items ?? [],
  })
}

const changeSize = async (item: WishlistItem, newVariant: Variant) => {
  if (!isEmpty(newVariant)) {
    item.variantId = newVariant.id

    await replaceItem(
      { productId: item.productId },
      { variantId: newVariant.id },
    )
  }
}

const deliveryEstimationInDays = computed(() =>
  getDeliveryEstimationInDays(shopConfiguration.value),
)

const addItemToCart = async (item: WishlistItem) => {
  const { variantId, product } = item

  if (!variantId) {
    $alert.show(
      i18n.t('basket.notification.add_basket'),
      Action.route,
      undefined,
    )
    return
  }

  item.variantId = null

  await basket.addItem({
    variantId,
    quantity: 1,
    customData: {
      ordernumber: getFormattedOrderNumber(product),
      deliveryEstimationDate: getDeliveryEstimation(
        getVariant(product.variants!, variantId),
        deliveryEstimationInDays.value,
      ),
    },
  })

  openAddToBasketFlyout(variantId)

  const variant = product.variants?.find((v) => v.id === variantId) as Variant

  if (!variant) {
    console.warn('Variant not found in product', product, variantId)
    return
  }

  if (product) {
    trackAddToBasket({
      product,
      variant,
      type: 'wishlist',
    })
  }
}

const metaTags = metaTagGenerator({
  robots: 'noindex,follow',
  canonical: sanitizeCanonical(`${config.public.baseUrl}${route.path}`),
})
const metaLinks = useStaticMetaLinks('wishlist')

useHead({
  title: 'Wishlist',
  ...metaTags,
  link: [...(metaTags.link || []), ...metaLinks.value],
})

defineOptions({ name: 'WishlistPage' })
definePageMeta({ pageType: 'wishlist' })
</script>
